import { useMemo } from 'react';

import { Box, Card, Link, Stack, CardContent, Grid2 as Grid } from '@mui/material';

import { useTranslate } from 'src/locales';
import { AuthLoader } from 'src/pages/auth/components/shared/AuthLoader';
import { PasswordField, ChannelStepLayout } from 'src/pages/channel/shared';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { useSmsStep3 } from '../hooks';
import { ProviderChip } from './provider-chip';
import { SectionHeader } from './section-header';
import { SmsCreditsInfo } from './sms-credits-info';
import { SetupGuideAlert } from './setup-guide-alert';
import { getSmsAccountHelpUrl, getSmsProviderConsoleLink } from '../utils/sms-provider-setup';

export function SMSStep3() {
  const { t } = useTranslate('translation');
  const { methods, isLoading, isNextDisabled, providerType, onSubmit } = useSmsStep3();

  const isBms = providerType === 'bms_api';
  const accountHelpUrl = getSmsAccountHelpUrl(providerType);

  const steps = useMemo(() => {
    const consoleLink = getSmsProviderConsoleLink(providerType);
    if (consoleLink) {
      return [
        <>
          {t('FIND_SMS_CONFIG_DETAILS')}{' '}
          <Link
            href={consoleLink.href}
            target="_blank"
            sx={{
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {t(consoleLink.labelKey)}
            <Iconify icon="mdi:open-in-new" width={16} height={16} />
          </Link>
        </>,
      ];
    }
    return [<span key="bms">{t('SMS_SETUP_GUIDE_BMS')}</span>];
  }, [providerType, t]);

  return (
    <>
      {isLoading && <AuthLoader />}

      <Stack spacing={4} sx={{ height: '100%' }}>
        <Form methods={methods} onSubmit={onSubmit}>
          <ChannelStepLayout
            title={t('SMS_CHANNEL_SETUP')}
            subtitle={t('CONFIGURE_YOUR_SMS_PROVIDER_DETAILS')}
            isNextDisabled={isNextDisabled}
          >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Card>
                <CardContent sx={{ p: 4 }}>
                  <Stack spacing={4}>
                    <ProviderChip provider={providerType} label={t('SMS_SERVICE_PROVIDER')} />

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Field.Text
                          name="channelName"
                          label={t('CHANNEL_NAME')}
                          placeholder={t('ENTER_CHANNEL_NAME_PLACEHOLDER')}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <SectionHeader
                          title={t('ACCOUNT_CONFIGURATION')}
                          tooltipTitle={accountHelpUrl ? t('LEARN_MORE_TOOLTIP') : undefined}
                          helpUrl={accountHelpUrl}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Field.Text
                          name="accountSid"
                          label={isBms ? t('SMS_BMS_FIELD_USER') : t('ACCOUNT_ID')}
                          placeholder={
                            isBms ? t('SMS_BMS_FIELD_USER_PLACEHOLDER') : t('ENTER_ACCOUNT_ID')
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <PasswordField
                          name="authToken"
                          label={isBms ? t('SMS_BMS_FIELD_PASSWORD') : t('AUTH_TOKEN')}
                          placeholder={
                            isBms ? t('SMS_BMS_FIELD_PASSWORD_PLACEHOLDER') : t('ENTER_AUTH_TOKEN')
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Field.Text
                          name="srcNumber"
                          label={isBms ? t('SMS_BMS_FIELD_SENDER') : t('SOURCE_NUMBER')}
                          placeholder={
                            isBms ? t('SMS_BMS_FIELD_SENDER_PLACEHOLDER') : t('ENTER_SOURCE_NUMBER')
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>

              <SetupGuideAlert title={t('SETUP_GUIDE')} steps={steps} />
              <SmsCreditsInfo />
            </Box>
          </ChannelStepLayout>
        </Form>
      </Stack>
    </>
  );
}
